import { useLazyQuery, useMutation, useApolloClient, useQuery } from '@apollo/client';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { DefaultEventsMap } from '@socket.io/component-emitter';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Progress,
  Spinner,
} from 'reactstrap';
import CustomButton from '../../../components/layout/CustomButton';
import {
  buildSelectionEntry,
  isApplicantSelectable,
  isApplicantSelected,
  pendingInterviewIdsOf,
} from '../JobApplicantCheckboxList';
import InputField from '../../../components/layout/InputField';
import { checkUserFeatureAccess, compactObject } from '../../../utils/commonFn';
import { ALL_MODAL_ACCCESS_SETTINGS } from '../../moduleAccess-settings/gql';

import SEARCH_IMG from '../../../assets/images/search.svg';

import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import Select from 'react-select';
import ConfirmPopUp from '../../../components/ConfirmPopUp';
import NoDataFound from '../../../components/layout/NoDataFound';
import Pagination from '../../../components/layout/Pagination';
import { AppContext } from '../../../context';
import {
  APPLICANT_STATUS,
  jobApplicantStatusForCandidate,
  PAGE_LIMIT,
  VIEW_PLACEMENT_MANAGEMENT_ROUTE,
} from '../../../utils/constant';
import ClientCandidateReasonModal from '../../candidate-pipeline/ClientCandidateReasonModal';
import CandidateCalendarInviteModal from '../../candidate-pipeline/interviews/calendar-invite/candidate/CandidateCalendarInviteModal';
import ClientCalendarInviteModal from '../../candidate-pipeline/interviews/calendar-invite/client/ClientCalendarInviteModal';
import {
  DECREMENT_IN_COUNT,
  UPDATE_INTERVIEW_FLAG,
} from '../../candidate-pipeline/interviews/gql';
import InterviewScheduleModal from '../../candidate-pipeline/interviews/InterviewScheduleModal';
import CreateEditOffer from '../../candidate-pipeline/offers/CreateEditOffer';
import { UPDATE_OFFER_STATUS, VERIFY_INTERVIEW } from '../../candidate-pipeline/offers/gql';
import InterviewViewDrawer from '../../candidate/application-flow/interview/InterviewViewDrawer';
import OfferViewDrawer from '../../candidate/application-flow/offers/OfferViewDrawer';
import SubmissionViewDrawer from '../../candidate/application-flow/submission/SubmissionViewDrawer';
import { GET_INTERVIEW_EMAILS } from '../../candidate/gql';
import AddRecruiterFolderModal from '../../recruiter-folder/folderComponents/AddRecruiterFolderModal';
import {
  BULK_REQUEST_AI_SCORES,
  CAN_BULK_ADD_TO_JOB_ORDER,
  GET_JOB_APPLICANTS_FOR_JOB,
  UPDATE_APPLICANTS_STATUS,
  UPDATE_APPLICANT_UPDATE,
  UPDATE_JOB_APPLICANT_STATUS_TO_INTERVIEW,
} from '../gql';
import JobApplicantDetailForJob from './JobApplicantDetailForJob';
import Loader from '../../../components/layout/Loader';
import ReplacementModal from '../../candidate/candidate-components/JobApplicationStatus/ReplacementModal';
import BulkAddModal from './bulk-add/BulkAddModal';

const DECLINED_APPLICANT_FILTER_VALUES = new Set<string>([
  'Declined',
  'Client Decline',
  'Candidate Decline',
]);

/** Empty multi-select: non-declined list, or [] when including declined (backend skips extra IN filter). */
function applicantStatusesForJobApplicantQuery(
  statusFilter: ReadonlyArray<{ value: string }>,
  includeDeclinedApplicants: boolean
): string[] {
  const selected =
    statusFilter?.map((item) => item.value).filter((v): v is string => Boolean(v)) ?? [];
  if (selected.length > 0) {
    return selected;
  }
  if (includeDeclinedApplicants) {
    return [];
  }
  return jobApplicantStatusForCandidate
    .filter((item) => !DECLINED_APPLICANT_FILTER_VALUES.has(item.value))
    .map((item) => item.value);
}

type ApplicantSortBy = 'NEWEST' | 'AI_SCORE';

const APPLICANT_SORT_OPTIONS: Array<{ value: ApplicantSortBy; label: string }> = [
  { value: 'NEWEST', label: 'Sort: Newest' },
  { value: 'AI_SCORE', label: 'Sort: AI Fit Score' },
];

const APPLIED_STATUS_OPTION = jobApplicantStatusForCandidate.find(
  (item) => item.value === 'Applied'
);

let time = 0;

const JobApplicantTab = ({
  jobOrder,
  isEdit = false,
  initialStatusFilter = [],
  initialCandidateName = '',
}: {
  jobOrder: any;
  isEdit?: boolean;
  initialStatusFilter?: Array<{ value: string; label: string }>;
  initialCandidateName?: string;
}) => {
  const _currentPage: number = parseInt('1');
  const [currentPage, setCurrentPage] = useState(_currentPage);
  const [limit, setLimit] = useState(10);
  const { user, theme } = useContext(AppContext);
  const { data: settingsData } = useQuery(ALL_MODAL_ACCCESS_SETTINGS);
  const hasAiScoring = checkUserFeatureAccess(user?.id, 'AI_MATCH_SCORING', settingsData);
  // Bulk Add access mirrors the upload endpoint's rule (creator, assigned
  // recruiter, company owner/sharee, privileged roles) via the API, so the
  // button is disabled up front instead of failing after an upload attempt.
  const { data: bulkAddAccessData, loading: bulkAddAccessLoading } = useQuery(
    CAN_BULK_ADD_TO_JOB_ORDER,
    {
      variables: { jobOrderId: jobOrder?.id },
      skip: !jobOrder?.id,
      fetchPolicy: 'network-only',
    }
  );
  const canBulkAdd = bulkAddAccessData?.canBulkAddToJobOrder === true;
  const [actionData, setActionData] = useState<any>([]);
  const [confirmBox, setConfirmBox] = useState(false);
  const { control, watch, getValues, resetField } = useForm<{
    currentBulkAction: string;
  }>({
    defaultValues: { currentBulkAction: '' },
  });
  const bulkStatusValue = watch('currentBulkAction');
  const [statusFilter, setStatusFilter] = useState<Array<any>>([]);
  const [recruiterFolderModal, setRecruiterFolderModal] = useState(false);
  const [pendingInterview, setPendingInterview] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(false);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [acountManagerPernmission, setAcountManagerPernmission] = useState<boolean>(false);

  //job order app history code
  const history = useHistory();
  const location = useLocation();
  const [sortBy, setSortBy] = useState<ApplicantSortBy>(() =>
    new URLSearchParams(location.search).get('sortBy') === 'AI_SCORE' ? 'AI_SCORE' : 'NEWEST'
  );
  // Never send AI_SCORE (or show it as selected) without the AI_MATCH_SCORING feature.
  const effectiveSortBy: ApplicantSortBy =
    hasAiScoring && sortBy === 'AI_SCORE' ? 'AI_SCORE' : 'NEWEST';
  // Bumped to force a reload; forceNetworkRef makes that reload bypass the cache.
  const [reloadTick, setReloadTick] = useState(0);
  const forceNetworkRef = useRef(false);
  const [applicantDetail, setApplicantDetail] = useState<any>({});
  const [rejectDetail, setRejectDetail] = useState<any>({});
  const [submissionViewToggle, setSubmissionViewToggle] =
    useState<boolean>(false);
  const [notesModal, setNotesModal] = useState<boolean>(false);
  const [firstInterview, setFirstInterview] = useState<boolean>(false);
  const [backToInterview, setBackToInterview] = useState<boolean>(false);
  const [candidateInterviewEmailData, setCandidateInterviewEmailData] =
    useState({});
  const [clientInterviewEmailData, setClientInterviewEmailData] = useState({});
  const [latestInterviewData, setLatestInterviewData] = useState<any>(null);
  const [interviewScheduleModal, setInterviewScheduleModal] =
    useState<boolean>(false);
  const [interviewDetailToggle, setInterviewDetailToggle] =
    useState<boolean>(false);
  const [calendarInviteModal, setCalendarInviteModal] =
    useState<boolean>(false);
  const [clientCalendarInviteModal, setClientCalendarInviteModal] =
    useState<boolean>(false);
  const [offerModal, setOfferModal] = useState<boolean>(false);
  const [offerDrawerToggle, setOfferDrawerToggle] = useState<boolean>(false);

  // for replacement workflow (ATS-1011)
  const [replacementModalToggle, setReplacementModalToggle] = useState<boolean>(false);
  const [placementId, setPlacementId] = useState<string>('');

  const [editOfferToggle, setEditOfferToggle] = useState<boolean>(false);
  const [counterOfferToggle, setCounterOfferToggle] = useState<boolean>(false);
  const [counterDecrementInPendingAction] = useMutation(DECREMENT_IN_COUNT);
  const [updateFlag] = useMutation(UPDATE_INTERVIEW_FLAG);
  const [latestOffer, setLatestOffer] = useState<any>(null);

  const [getInterviewEmails, { data: getInterviewEmailsData }] =
    useLazyQuery(GET_INTERVIEW_EMAILS);
  const [updateApplicantStatus] = useMutation(UPDATE_APPLICANT_UPDATE);
  const [updateOfferStatusMutation] = useMutation(UPDATE_OFFER_STATUS);

  const [updateApplicantsStatus] = useMutation(UPDATE_APPLICANTS_STATUS);
  const [searchParams, setSearchParams] = useState<string>('');
  const [delecinedCheckBox, setDeclinedCheckBox] = useState<boolean>(false);
  const [localDeclinedIds, setLocalDeclinedIds] = useState<Set<string>>(new Set());
  const [verifyInterview] = useMutation(VERIFY_INTERVIEW);
  const [bulkRequestAiScores] = useMutation(BULK_REQUEST_AI_SCORES);
  // Non-null while a bulk AI fit-score run is in flight; doubles as the re-entry guard.
  const [aiScoreProgress, setAiScoreProgress] = useState<{ done: number; total: number } | null>(
    null
  );
  const aiScoreRunningRef = useRef(false);

  const [
    getJobApplicantsForJob,
    { data: jobApplicantsList, loading: waiting, refetch },
  ] = useLazyQuery(GET_JOB_APPLICANTS_FOR_JOB);

  const [updateJobApplicantStatusInterview] = useMutation(
    UPDATE_JOB_APPLICANT_STATUS_TO_INTERVIEW
  );

  const apolloClient = useApolloClient();
  const socket = useRef<Socket<DefaultEventsMap, DefaultEventsMap> | undefined>();

  useEffect(() => {
    if (!socket.current || !socket.current.connected) {
      socket.current = io(
        process.env.REACT_APP_WEBSOCKET_BASEURL || 'localhost:3001',
        { path: '/websockets', transports: ['websocket'] }
      );
    }

    const handleStatusUpdate = (data: { applicantId: string; status: string; jobOrderId: string }) => {
      if (data.jobOrderId !== jobOrder?.id) return;
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({ __typename: 'JobApplicant', id: data.applicantId }),
        fields: {
          JobAppStatus: () => data.status,
        },
      });
    };

    socket.current.on('applicant-status-updated', handleStatusUpdate);
    return () => {
      socket.current?.off('applicant-status-updated', handleStatusUpdate);
    };
  }, [jobOrder?.id, apolloClient]);

  const getJobOrderApplicants = () => {
    const status = applicantStatusesForJobApplicantQuery(
      statusFilter,
      delecinedCheckBox
    );

    if (jobOrder?.id) {
      const forceNetwork = forceNetworkRef.current;
      forceNetworkRef.current = false;
      getJobApplicantsForJob({
        variables: {
          limit,
          page: currentPage,
          jobOrderId: jobOrder?.id,
          status: status.length ? status : 'All',
          searchParams: searchParams || '',
          pendingInterview,
          pendingSubmission,
          sortBy: effectiveSortBy,
        },
        ...(forceNetwork ? { fetchPolicy: 'network-only' as const } : {}),
      });
    }

  };

  const syncStatusFilterToUrl = (selected: Array<{ value: string; label: string }> | null) => {
    const params = new URLSearchParams(location.search);
    if (selected?.length) {
      params.set('statusFilter', selected.map((s) => s.value).join(','));
    } else {
      params.delete('statusFilter');
    }
    history.replace({ search: params.toString() });
  };

  const syncSortByToUrl = (value: ApplicantSortBy) => {
    const params = new URLSearchParams(location.search);
    if (value === 'AI_SCORE') {
      params.set('sortBy', 'AI_SCORE');
    } else {
      params.delete('sortBy');
    }
    history.replace({ search: params.toString() });
  };

  const handleBulkAddClose = ({ completed }: { completed: boolean }) => {
    setBulkAddOpen(false);
    if (completed) {
      // Show the freshly added applicants: Applied filter, best AI fit first.
      const params = new URLSearchParams(location.search);
      if (APPLIED_STATUS_OPTION) {
        setStatusFilter([APPLIED_STATUS_OPTION]);
        params.set('statusFilter', APPLIED_STATUS_OPTION.value);
      }
      setPendingInterview(false);
      setPendingSubmission(false);
      setCurrentPage(1);
      if (hasAiScoring) {
        setSortBy('AI_SCORE');
        params.set('sortBy', 'AI_SCORE');
      }
      history.replace({ search: params.toString() });
    }
    // The lazy query is cache-first and the states above may not change, so
    // bump the reload tick and bypass the cache for that one load.
    forceNetworkRef.current = true;
    setReloadTick((t) => t + 1);
  };

  const setQueryParams = (searchParams) => {
    const criteria = compactObject({
      currentPage,
      searchParams,
      statusFilter,
    });
    const urlQueryParams: any = [];
    if (Object.keys(criteria).length) {
      for (const key in criteria) {
        if (
          Object.prototype.hasOwnProperty.call(criteria, key) &&
          criteria[key]
        ) {
          urlQueryParams.push(`${key}=${criteria[key]}`);
        }
      }
    }
  };

  const searchControl = (value: string) => {
    setQueryParams(value);
    setSearchParams(value);
    setCurrentPage(1);
    const status = applicantStatusesForJobApplicantQuery(
      statusFilter,
      delecinedCheckBox
    );
    if (!value || value?.length > 2) {
      window.clearTimeout(time);
      time = window.setTimeout(() => {
        if (jobOrder) {
          getJobApplicantsForJob({
            variables: {
              limit,
              page: currentPage,
              jobOrderId: jobOrder?.id,
              status,
              searchParams: value,
              pendingInterview,
              pendingSubmission,
              sortBy: effectiveSortBy,
            },
          });
        }
      }, 1000);
    }
  };

  const handleBulkAction = async () => {
    const status = getValues('currentBulkAction');
    if (status !== 'Clear Pending Interviews') {
      const objectTypeData = actionData?.filter((data) => typeof data !== 'string');
      const applicantIds = objectTypeData.map((data) => data.userId);
      const res = await updateApplicantsStatus({
        variables: { data: { applicantIds, status } },
      });
      if (res?.errors?.length) {
        toast.error(res.errors[0]?.message || 'Unable to update status.');
      } else if (res?.data) {
        toast.success(res.data?.updateApplicantsStatus?.message || 'Successfully Updated!');
        searchControl('');
      }
      resetField('currentBulkAction');
      setActionData([]);
      searchControl('');
    } else {
      const pendingInterviewIds: [string] = actionData?.filter(
        (data) => typeof data === 'string'
      );
      const verifiedInterviewResponse = await verifyInterview({
        variables: { verifyInterviewInput: { interviewIds: pendingInterviewIds } },
      });
      if (verifiedInterviewResponse?.data?.verifyInterview?.status === 200) {
        getJobOrderApplicants();
        toast.success('Successfully Updated!');
      }
      resetField('currentBulkAction');
      setActionData([]);
      searchControl('');
    }
  };

  // "Select all on this page" (kan-116): the rows a user could tick by hand on the
  // current page, and whether they all / some are already in the selection.
  const pageRows: any[] = (jobApplicantsList?.getApplicantsOfJob?.jobApplicants || []).filter(
    (item: any) => !localDeclinedIds.has(item.id)
  );
  const selectableRows = pageRows.filter(isApplicantSelectable);
  const allOnPageSelected =
    selectableRows.length > 0 && selectableRows.every((row) => isApplicantSelected(row, actionData));
  const someOnPageSelected = selectableRows.some((row) => isApplicantSelected(row, actionData));

  const toggleSelectAllOnPage = (checked: boolean) => {
    const pageIds = new Set(selectableRows.map((row) => row.id));
    const pageInterviewIds = new Set(selectableRows.flatMap((row) => pendingInterviewIdsOf(row)));
    // Drop this page's entries first so the operation is idempotent either way.
    const others = (actionData || []).filter((entry: any) =>
      typeof entry === 'string' ? !pageInterviewIds.has(entry) : !pageIds.has(entry?.userId)
    );
    if (!checked) {
      setActionData(others);
      return;
    }
    const added: any[] = selectableRows.flatMap((row): any[] =>
      row.JobAppStatus === 'INTERVIEWED' ? pendingInterviewIdsOf(row) : [buildSelectionEntry(row)]
    );
    setActionData([...others, ...added]);
  };

  // One applicant per request, 3 requests in flight at a time (= the API's own scoring
  // concurrency): same throughput as batching, but the counter ticks per applicant and the
  // per-row spinner shows exactly who is being scored right now.
  const AI_SCORE_CONCURRENCY = 3;
  const [aiScoringIds, setAiScoringIds] = useState<Set<string>>(new Set());

  // A refresh mid-run only loses the chunks not yet sent; warn before leaving.
  useEffect(() => {
    if (!aiScoreProgress) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [aiScoreProgress]);

  const handleBulkAiScore = async () => {
    if (aiScoreRunningRef.current) return;

    // Selection entries are { userId: <jobApplicantId>, ... } objects; INTERVIEWED rows
    // contribute pending-interview id strings instead, which are not applicants.
    const candidates: any[] = (actionData || []).filter(
      (x) => x && typeof x === 'object' && x.userId
    );
    const selectedIds: string[] = Array.from(
      new Set<string>(candidates.map((x) => x.userId as string))
    );
    const rowsById = new Map<string, any>(
      (jobApplicantsList?.getApplicantsOfJob?.jobApplicants || []).map((row: any) => [
        row.id,
        row,
      ])
    );
    // A row we can see with a score is skipped up front; rows not on the current page
    // are sent anyway and the API skips them if they are already scored.
    const unscoredIds = selectedIds.filter((id) => {
      const row = rowsById.get(id);
      return !row || row.aiScore == null;
    });
    const locallySkipped = selectedIds.length - unscoredIds.length;

    if (!unscoredIds.length) {
      toast.info('All selected applicants already have an AI fit score.');
      return;
    }

    const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
    const skipNote =
      locallySkipped > 0
        ? ` ${plural(locallySkipped, 'applicant')} already scored will be skipped.`
        : '';
    const proceed = window.confirm(
      `Get AI fit scores for ${plural(unscoredIds.length, 'applicant')}?${skipNote}`
    );
    if (!proceed) return;

    aiScoreRunningRef.current = true;
    setAiScoreProgress({ done: 0, total: unscoredIds.length });
    let scored = 0;
    let skipped = locallySkipped;
    let failed = 0;
    try {
      const queue = [...unscoredIds];
      const inFlight = new Set<string>();
      let done = 0;
      let firstError: Error | null = null;

      const scoreOne = async (id: string) => {
        inFlight.add(id);
        setAiScoringIds(new Set(inFlight));
        try {
          const res = await bulkRequestAiScores({ variables: { jobApplicantIds: [id] } });
          if (res?.errors?.length) {
            throw new Error(res.errors[0]?.message || 'Unable to get AI fit scores.');
          }
          const result = res?.data?.bulkRequestAiScores;
          scored += result?.scored ?? 0;
          skipped += result?.skipped ?? 0;
          failed += result?.failed ?? 0;
        } catch (err: any) {
          // A transport/GraphQL failure for one applicant counts as failed; keep going.
          failed += 1;
          if (!firstError) firstError = err instanceof Error ? err : new Error(String(err));
        } finally {
          inFlight.delete(id);
          setAiScoringIds(new Set(inFlight));
          done += 1;
          setAiScoreProgress({ done, total: unscoredIds.length });
          // Show the new badge right away rather than only at the very end.
          refetch?.();
        }
      };

      const worker = async () => {
        while (queue.length) {
          const id = queue.shift();
          if (id) await scoreOne(id);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(AI_SCORE_CONCURRENCY, unscoredIds.length) }, worker)
      );
      if (firstError && failed === unscoredIds.length) throw firstError;

      const summary = `AI fit scores: ${scored} scored, ${skipped} skipped, ${failed} failed`;
      if (failed > 0) toast.warn(summary);
      else toast.success(summary);
      refetch?.();
      setActionData([]);
    } catch (error: any) {
      toast.error(error?.message || 'Unable to get AI fit scores.');
      refetch?.();
    } finally {
      aiScoreRunningRef.current = false;
      setAiScoreProgress(null);
      setAiScoringIds(new Set());
    }
  };

  useEffect(() => {
    if (jobOrder) {
      document.title = 'Loading...';
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getJobOrderApplicants();

    // eslint-disable-next-line
  }, [
    statusFilter,
    limit,
    currentPage,
    pendingInterview,
    pendingSubmission,
    delecinedCheckBox,
    effectiveSortBy,
    reloadTick,
  ]);

  useEffect(() => {
    if (jobOrder?.id) {
      if (initialStatusFilter?.length) setStatusFilter(initialStatusFilter);
      if (initialCandidateName) setSearchParams(initialCandidateName);
    }

    // eslint-disable-next-line
  }, [jobOrder?.id]);

  useEffect(() => {
    if (jobOrder?.jobTitle)
      document.title = `${jobOrder?.jobTitle || 'Job'} - Applicants -RealREPP`;

    // eslint-disable-next-line
  }, [jobOrder?.jobTitle]);

  useEffect(() => {
    if (!actionData?.length) {
      resetField('currentBulkAction');
    }
  }, [actionData?.length, resetField]);

  // eslint-disable-next-line array-callback-return
  const filteredObj = actionData?.filter((item) => {
    if (item?.url) {
      if (!item?.url?.includes('linkedin')) {
        return item;
      }
    }
  });

  const filteredEmails = filteredObj.map((item) => {
    return item?.url;
  });

  const customStyles = {
    control: (styles, provided) => ({
      ...styles,
      background: theme === 'light' ? provided.background : '#282828',
      borderColor: theme === 'light' ? '#ced4da' : '#525252',
      color: theme === 'light' ? provided.background : '#d9d9d9',
      fontSize: 12,
      '&:hover': {
        borderColor: theme === 'light' ? '#ced4da' : '#525252',
        color: theme === 'light' ? provided.background : '#d9d9d9',
      },
    }),
    menu: (provided) => ({
      ...provided,
      background: theme === 'light' ? provided.background : '#282828',
      zIndex: 999999,
      color: theme === 'light' ? provided.background : '#d9d9d9',
    }),
    option: (provided) => ({
      ...provided,
      fontSize: 12,
      background: theme === 'light' ? provided.background : '#363636',
      '&:hover': {
        background: theme === 'light' ? provided.background : '#474444',
        color: theme === 'light' ? provided.background : '#d9d9d9',
      },
    }),
    container: (provided, state) => ({
      ...provided,
      fontSize: 12,
      minWidth: 200,
      maxWidth: 'auto',
    }),
  };

  const updateStatusToInterview = async (id: string) => {
    const res = await updateJobApplicantStatusInterview({
      variables: { id },
    });

    if (res?.data) {
      toast.success('The job app status is set to Interviewed');
    }
  };

  const actionHandler = (data, type) => {
    setApplicantDetail({ ...data, JobOrder: jobOrder, type });

    switch (type) {
      case 'View Submission Detail':
        setSubmissionViewToggle(true);
        break;

      case 'Client Decline':
        setRejectDetail({ ...data, type });
        setNotesModal(!notesModal);
        break;

      case 'Candidate Decline':
        setRejectDetail({ ...data, type });
        setNotesModal(!notesModal);
        break;

      case 'Schedule First Interview':
        interviewSpecificDetail({ ...data, type });
        setFirstInterview(true);
        setInterviewScheduleModal(true);
        break;

      case 'View Interview Details':
        interviewSpecificDetail({ ...data, type });
        getLatestInterview({ ...data, type });
        setInterviewDetailToggle(true);
        break;

      case 'Schedule Next Interview':
        interviewSpecificDetail({ ...data, type });
        setFirstInterview(false);
        setInterviewScheduleModal(true);
        break;

      case 'Back To Interview':
        updateStatusToInterview(data?.id)
        refetch()
        break;

      case 'Send Candidate Invite':
        getLatestInterview({ ...data, type });
        setCalendarInviteModal(true);
        break;

      case 'Send Client Invite':
        getLatestInterview({ ...data, type });
        setClientCalendarInviteModal(true);
        break;

      case 'Clear Pending Client Action':
        clientFlag({ data, clientInviteFlag: true });
        break;

      case 'Clear Pending Candidate Action':
        clientFlag({ data, candidateInviteFlag: true });
        break;
      case 'Clear Pending Interviews':
        findPendingInterview({ ...data })
        break;

      case 'Convert to Offer':
        latestOfferFn({ ...data, type });
        getLatestInterview({ ...data, type });
        setOfferModal(!offerModal);
        break;

      case 'View Offer Details':
        setOfferDrawerToggle(true);
        break;

      case 'Move To Placement':
        updateOfferToPlacement({ ...data, JobOrder: jobOrder, type });
        // setPlacementMoveToggle(true);
        break;

      case 'Update Offer':
        latestOfferFn({ ...data, type });
        setCounterOfferToggle(false);
        setEditOfferToggle(!editOfferToggle);
        setOfferModal(!offerModal);
        break;

      case 'Counter Offer':
        latestOfferFn({ ...data, type });
        setEditOfferToggle(false);
        setCounterOfferToggle(true);
        setOfferModal(true);
        break;
      default:
        setConfirmBox(true);
        break;
    }
  };
  const findPendingInterview = (pendingInterview) => {
    let pendingInterviews = pendingInterview?.interviews.filter(interview => interview?.InterviewState === "PENDING").map(pendingInterview => pendingInterview?.id);
    veriyinterviews(pendingInterviews)
  }

  const veriyinterviews = async (pendingInterviewIds) => {
    let verifiedInterviewResponse = await verifyInterview({
      variables: { verifyInterviewInput: { interviewIds: pendingInterviewIds } },
    });
    if (verifiedInterviewResponse?.data?.verifyInterview?.status === 200) {
      refetch()
      setActionData([])
      toast.success('Successfully Updated!');
    }

  }
  const interviewSpecificDetail = (data) => {
    const actualJobLocation = jobOrder?.companyLocation
      ? ['US', null, undefined, ''].includes(jobOrder?.companyLocation.country) ? `${data?.JobOrder?.companyLocation?.streetAddress}, ${jobOrder?.companyLocation?.city}, ${jobOrder?.companyLocation?.state}, ${jobOrder?.companyLocation?.zipCode}` : `${data?.JobOrder?.companyLocation?.streetAddress}, ${jobOrder?.companyLocation?.city}, ${jobOrder?.companyLocation?.zipCode}, ${jobOrder?.companyLocation?.country}`
      : '--';
    setApplicantDetail({
      ...data,
      jobTimezone: jobOrder?.timezone,
      jobTimezoneId: jobOrder?.timezoneId,
      jobDstOffset: jobOrder?.dstOffset,
      jobOrderId: jobOrder?.id,
      candidateId: data?.candidate?.id,
      companyId: jobOrder?.company?.id,
      jobApplicantId: data?.id,
      company: jobOrder?.company?.name || '',
      jobTitle: jobOrder?.jobTitle || '',
      name: data?.candidate?.fullName || '',
      jobLocation: actualJobLocation,
      workplaceTypes: jobOrder?.workplaceTypes
    });
  };

  const getLatestInterview = (data) => {
    let interview =
      data?.interviews?.length && data?.interviews?.find((item) => !item?.locked);
    if (interview) {
      let interviewDate = {
        interviewDateTime: moment(interview?.interviewDateTime)
          .utcOffset(interview?.timeZoneId)
          .format('MM/DD/YYYY'),
        time: moment(interview?.interviewDateTime)
          .utcOffset(interview?.timeZoneId)
          .format('hh:mm A'),
      };
      setLatestInterviewData({ ...interview, ...interviewDate });
    } else {
      setLatestInterviewData(null);
    }
  };

  const latestOfferFn = (data) => {
    let latest = data?.offers?.find((item) => !item?.locked);
    setLatestOffer(latest);
  };

  const clientFlag = async (value) => {
    let interview = value?.data?.interviews?.find((item) => !item?.locked);

    if (interview) {
      const { data } = await counterDecrementInPendingAction({
        variables: { category: 'Interview' },
      });
      let UpdateInterviewFlagInput: any = {};
      if (value?.candidateInviteFlag) {
        UpdateInterviewFlagInput = {
          interviewId: interview?.id,
          candidateInviteFlag: value?.candidateInviteFlag,
        };
      } else if (value?.clientInviteFlag) {
        UpdateInterviewFlagInput = {
          interviewId: interview?.id,
          clientInviteFlag: value?.clientInviteFlag,
        };
      }

      await updateFlag({ variables: { UpdateInterviewFlagInput } });

      if (data) {
        const { status } = data.counterDecrementInPendingAction;
        if (status === 200) {
          toast.success('Updated Successfully!');
          // searchControl('');
        }
      }
    }
  };

  const changeApplicantStatus = async () => {
    let { id, type } = applicantDetail;
    let res = await updateApplicantStatus({
      variables: { applicantId: id, status: type === "Submit_Again" ? "SUBMITTED" : type },
    });
    if (res?.errors?.length) {
      setConfirmBox(false);
      toast.error(res.errors[0]?.message || 'Unable to update status.');
    } else if (res?.data) {
      setConfirmBox(false);
      toast.success('Successfully Updated!');
      refetch();
    }
  };

  // this method is used for the convert the latest offer into offer
  const updateOfferToPlacement = useCallback(async (data?: { offers?: string, id?: string, replacementId?: string }, isReplacement: boolean = true) => {
    const latestOffer = (data?.offers || applicantDetail?.offers)?.find((item) => !item?.locked);
    if (!latestOffer) {
      toast.warn("Placement offer is not locked")
      return
    };
    const response = await updateOfferStatusMutation({
      variables: {
        updatingOfferStatusInput: {
          id: latestOffer?.id,
          jobApplicantId: data?.id || applicantDetail?.id,
          status: 'MOVE_TO_PLACEMENT',
          replacementId: data?.replacementId,
          isReplacement,
        },
      },
    });

    if (response?.data) {
      const updatingOfferStatus = response?.data?.updatingOfferStatus;
      // status 202 for replacement workflow (ATS-1011)
      if (updatingOfferStatus?.status === 202) {
        setReplacementModalToggle(!replacementModalToggle);
        setPlacementId(updatingOfferStatus?.placementId);
      } else if (updatingOfferStatus?.status === 400) {
        setReplacementModalToggle(false);
        toast.error(updatingOfferStatus?.message || 'Unable to move to placement.');
      } else {
        toast.success('Offer status updated successfully');
        // supposing we are receiving an id of placement and view that placement
        if (updatingOfferStatus?.placementId) {
          history.push({
            pathname: `${VIEW_PLACEMENT_MANAGEMENT_ROUTE}/${updatingOfferStatus?.placementId}`,
          });
        }
      }
    }
  }, [applicantDetail?.offers, applicantDetail?.id, updateOfferStatusMutation, replacementModalToggle, history]);

  useEffect(() => {
    if (getInterviewEmailsData?.getInterviewEmails?.interviewEmails?.length) {
      const clientInterviewEmail =
        getInterviewEmailsData?.getInterviewEmails?.interviewEmails?.find(
          (item: { isToClient: boolean }) => item?.isToClient
        );

      const candidateInterviewEmails =
        getInterviewEmailsData?.getInterviewEmails?.interviewEmails?.find(
          (item: { isToClient: boolean }) => !item?.isToClient
        );

      if (candidateInterviewEmails) {
        setCandidateInterviewEmailData(candidateInterviewEmails);
      }

      if (clientInterviewEmail) {
        let newObj: any = { ...clientInterviewEmail };
        newObj.attachingFromCandidateEmails = false;
        setClientInterviewEmailData(newObj);
      } else {
        let newObj: any = { ...candidateInterviewEmails };
        newObj.attachingFromCandidateEmails = true;
        setClientInterviewEmailData(newObj);
      }
    }

    // eslint-disable-next-line
  }, [getInterviewEmailsData]);

  useEffect(() => {
    if (latestInterviewData) {
      getInterviewEmails({
        variables: { interviewId: latestInterviewData?.id },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestInterviewData]);

  return (
    <div>
      <div className="d-flex justify-content-start align-items-start flex-wrap gap-10 primaryHeaderSpacing">
        <div>
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <img src={SEARCH_IMG} alt="search" />
              </InputGroupText>
            </InputGroupAddon>

            <Input
              type="text"
              className="search-by-company"
              placeholder="Search by name or email"
              value={searchParams}
              onChange={(e) => {
                searchControl(e.target.value);
                setSearchParams(e.target.value);
              }}
            />
          </InputGroup>

          <small className="text-info">
            {searchParams &&
              searchParams.length <= 2 &&
              ' Please add more than two characters'}
          </small>
        </div>

        <div className="filter-status" style={{ flexWrap: 'wrap' }}>
          {/* <div className="filter-by"> */}
          <div className="jobapp-category-dropdown mt-md-0">
            <Select
              isMulti
              isSearchable
              isClearable
              styles={customStyles}
              options={jobApplicantStatusForCandidate}
              value={statusFilter?.length ? statusFilter : null}
              onChange={(val: any) => {
                setStatusFilter(val);
                setCurrentPage(1);
                syncStatusFilterToUrl(val);
              }}
              isDisabled={pendingSubmission || pendingInterview}
            />
          </div>

          {hasAiScoring && (
            <div className="jobapp-category-dropdown mt-md-0 ml-3">
              <Select
                isSearchable={false}
                isClearable={false}
                styles={customStyles}
                options={APPLICANT_SORT_OPTIONS}
                value={APPLICANT_SORT_OPTIONS.find((o) => o.value === effectiveSortBy)}
                onChange={(val: any) => {
                  const next: ApplicantSortBy = val?.value === 'AI_SCORE' ? 'AI_SCORE' : 'NEWEST';
                  setSortBy(next);
                  setCurrentPage(1);
                  syncSortByToUrl(next);
                }}
              />
            </div>
          )}

          {/* <div className='mx-3'></div> */}

          <div className="jobapp-category-dropdown mt-md-0 d-flex flex-wrap gap-10">
            <button
              style={{
                backgroundColor: `${pendingSubmission ? '#f4f3f3' : 'white'}`,
                color: `${pendingSubmission ? '#eaa827' : '#495057'}`,
                border: `${'1px solid #ced4da'}`,
              }}
              className="filter-job-order ml-3"
              onClick={() => {
                setPendingSubmission(!pendingSubmission);
                setPendingInterview(false);
              }}
              type="button"
            >
              My Pending Submissions
            </button>

            <button
              style={{
                backgroundColor: `${pendingInterview ? '#f4f3f3' : 'white'}`,
                color: `${pendingInterview ? '#eaa827' : '#495057'}`,
                border: `${'1px solid #ced4da'}`,
              }}
              className="filter-job-order ml-3"
              onClick={() => {
                setPendingInterview(!pendingInterview);
                setPendingSubmission(false);
              }}
              type="button"
            >
              My Pending Interview
            </button>
          </div>

          <button
            className="buttonGenericStyle filledButton ml-sm-3 mt-md-0"
            onClick={() => {
              setStatusFilter([]);
              setSearchParams('');
              setPendingInterview(false);
              setPendingSubmission(false);
              setSortBy('NEWEST');
              setCurrentPage(1);
              const params = new URLSearchParams(location.search);
              params.delete('statusFilter');
              params.delete('sortBy');
              history.replace({ search: params.toString() });
            }}
          >
            Clear
          </button>

          <div className=' d-flex align-items-center mx-5 gap-10'>
            <Input
              style={{ height: '20px', width: '20px' }}
              type="checkbox"
              checked={delecinedCheckBox}
              onChange={(e) => {
                setDeclinedCheckBox(e.target.checked)
                setCurrentPage(1)
              }}
            />
            <span className="checkboxTextStyle pl-lg-0 pl-2 ml-2">
              Included Declined Apps
            </span>
          </div>

          {jobOrder?.id && (
            <button
              type="button"
              className="buttonGenericStyle filledButton mt-md-0"
              onClick={() => setBulkAddOpen(true)}
              disabled={bulkAddAccessLoading || !canBulkAdd}
              title={
                bulkAddAccessLoading
                  ? 'Checking access…'
                  : canBulkAdd
                    ? undefined
                    : 'You do not have access to this job order'
              }
            >
              Bulk Add
            </button>
          )}

        </div>
      </div>

      {!waiting && selectableRows.length > 0 && (
        <div className="d-flex align-items-center mt-3 ml-1" style={{ gap: 10 }}>
          <Input
            type="checkbox"
            id="selectAllApplicantsOnPage"
            style={{ height: '20px', width: '20px', margin: 0, position: 'static' }}
            checked={allOnPageSelected}
            innerRef={(el: HTMLInputElement | null) => {
              if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected;
            }}
            disabled={!!aiScoreProgress}
            onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
          />
          <label htmlFor="selectAllApplicantsOnPage" className="checkboxTextStyle m-0">
            Select all on this page ({selectableRows.length})
          </label>
        </div>
      )}

      {actionData?.length >= 1 && (
        <div className="sticky-action-container mt-3">
          <div className="d-selected-num bulk_actions_label">Bulk Actions:</div>
          <div className="d-selected-num">{actionData?.length} Selected</div>
          <div className="sticky-action-btns">
            {filteredEmails.length ? (
              <a
                href={
                  filteredEmails?.length
                    ? `mailto:${filteredEmails.join(',')}`
                    : '#'
                }
                className="m-1 d-block bg-light text-dark p-2 py-1 btn-sm"
              >
                Send Email
              </a>
            ) : (
              <button
                type="button"
                disabled={true}
                className="m-1 d-block bg-light text-dark p-2 py-1 btn-sm disabled-btn"
              >
                Send Email
              </button>
            )}
            <Button
              className="m-1 d-block px-3 py-1 border-0 buttonGenericStyle filledButton"
              onClick={() => setRecruiterFolderModal(!recruiterFolderModal)}
            >
              Add to Recruiter Folder
            </Button>

            <Button
              className="m-1 d-block bg-light text-dark px-3 py-1 btn-sm border-0"
              onClick={() => veriyinterviews(actionData)}
              disabled={!acountManagerPernmission}

            >
              Clear Pending Interviews
            </Button>

            {hasAiScoring && (
              <Button
                className="m-1 d-block bg-light text-dark px-3 py-1 btn-sm border-0"
                onClick={handleBulkAiScore}
                disabled={!!aiScoreProgress}
              >
                {aiScoreProgress
                  ? `Scoring ${aiScoreProgress.done}/${aiScoreProgress.total}…`
                  : 'AI Fit Score'}
              </Button>
            )}
          </div>

          <Controller
            name="currentBulkAction"
            control={control}
            render={({ field }) => (
              <InputField
                style={{ minWidth: 'max-content' }}
                className="save-changes"
                inputtype="select"
                selectItems={APPLICANT_STATUS}
                inputid="currentBulkAction"
                disable={!actionData?.length}
                inputValue={field.value}
                placeholder="Status Update"
                inputMethod={field.onChange}
              />
            )}
          />

          <CustomButton
            buttonText="Update"
            buttonType="button"
            buttonColor="primary"
            disabled={!(bulkStatusValue && actionData?.length)}
            className="save-changes ml-3 mr-2"
            buttonClick={handleBulkAction}
          />
        </div>
      )}

      {aiScoreProgress && (
        <div
          className="mt-2 px-3 py-2"
          role="status"
          aria-live="polite"
          style={{
            border: '1px solid #f3d9a4',
            background: '#fffbf0',
            borderRadius: 6,
          }}
        >
          <div className="d-flex align-items-center mb-2" style={{ gap: 10, fontSize: 13 }}>
            <Spinner size="sm" color="warning" />
            <strong>Scoring AI fit…</strong>
            <span>
              {aiScoreProgress.done} of {aiScoreProgress.total} done
            </span>
            <span className="text-muted ml-auto">
              Please keep this page open until it finishes.
            </span>
          </div>
          <Progress
            animated
            color="warning"
            value={aiScoreProgress.done}
            max={aiScoreProgress.total}
            style={{ height: 6 }}
          />
        </div>
      )}

      {!waiting &&
        !!jobApplicantsList?.getApplicantsOfJob?.jobApplicants?.length &&
        jobApplicantsList?.getApplicantsOfJob?.jobApplicants
          .filter((item) => !localDeclinedIds.has(item.id))
          .map((item) => {
          return (
            // kan-120: key by id, not index — declining a row otherwise shifts
            // later applicants into reused instances whose local state (AI
            // badge score, modals, drawer) belonged to the previous applicant.
            <JobApplicantDetailForJob
              key={item.id}
              data={{ ...item, JobOrder: jobOrder }}
              actionHandler={actionHandler}
              setActionData={setActionData}
              actionData={actionData}
              isEdit={isEdit}
              setAcountManagerPernmission={setAcountManagerPernmission}
              onDeclined={(id: string) =>
                setLocalDeclinedIds((prev) => { const next = new Set(prev); next.add(id); return next; })
              }
              aiScoring={aiScoringIds.has(item.id)}
            />
          )
        })}

      {!waiting &&
        !jobApplicantsList?.getApplicantsOfJob?.jobApplicants?.length && (
          <NoDataFound text="No Applicant Found!" />
        )}

      {waiting &&
        !jobApplicantsList?.getApplicantsOfJob?.jobApplicants?.length && (
          <div
            style={{ maxHeight: '50vh' }}
            className="d-flex align-items-center justify-content-center"
          >
            <Loader />
          </div>
        )}

      {jobApplicantsList?.getApplicantsOfJob?.jobApplicants?.length ? (
        <div className="users-pagination">
          <Pagination
            onPageChange={(pageClicked: number) => {
              setCurrentPage(pageClicked);
            }}
            pageCount={Math.ceil(
              jobApplicantsList?.getApplicantsOfJob?.count / limit
            )}
            currentPage={currentPage}
          />

          <div className="d-flex align-items-center">
            <div className="pagination-display paginationItemsSelect">
              <p className="m-0">
                {(currentPage - 1) * limit + 1} to{' '}
                {Math.min(
                  currentPage * limit,
                  jobApplicantsList?.getApplicantsOfJob?.count
                )}{' '}
                of {jobApplicantsList?.getApplicantsOfJob?.count}
              </p>

              <div className="divider"></div>

              <label>Display</label>

              <InputField
                label=""
                inputtype="select"
                selectItems={PAGE_LIMIT.filter((item) => item.value !== limit)}
                inputid="limit"
                placeholder={limit.toString()}
                inputMethod={(value: string) => {
                  setQueryParams(searchParams);
                  setCurrentPage(1);
                  setLimit(parseInt(value));
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {recruiterFolderModal && (
        <AddRecruiterFolderModal
          isOpen={recruiterFolderModal}
          toggle={() => setRecruiterFolderModal(!recruiterFolderModal)}
          recordToAdd={actionData}
          refetch={getJobOrderApplicants}
          clearRecord={() => setActionData([])}
        />
      )}



      {/* add modal for the submission action and interview  */}

      {/* submission view drawer  */}
      {submissionViewToggle && (
        <SubmissionViewDrawer
          refetchOwner={null}
          toggle={() => setSubmissionViewToggle(!submissionViewToggle)}
          isOpen={submissionViewToggle}
          applicantData={applicantDetail}
          setCandidateFilter={setStatusFilter}
          refetch={() => getJobOrderApplicants()}
        />
      )}

      {/* this drawer is used for the schedule interview for the first time and for schedule interview for the next */}
      {applicantDetail && interviewScheduleModal && (
        <InterviewScheduleModal
          accountManagerAndCandidateRecruiterSame={
            applicantDetail?.JobOrder?.company?.companyOwner?.owner?.id ===
            applicantDetail?.candidate?.recruiterOwner?.id
          }
          setCandidateFilter={setStatusFilter}
          firstInterview={firstInterview}
          backToInterview={backToInterview}
          candidateId={applicantDetail?.candidate?.id}
          interviewScheduleModal={interviewScheduleModal}
          applicantData={applicantDetail}
          interviewScheduleModalToggle={() =>
            setInterviewScheduleModal(!interviewScheduleModal)
          }
          refetch={() => getJobOrderApplicants()}
          closeModel={() => setInterviewScheduleModal(false)}
        />
      )}

      {/* this drawer is used for client calander decline  */}
      {notesModal && (
        <ClientCandidateReasonModal
          refetch={() => getJobOrderApplicants()}
          modal={notesModal}
          toggle={() => {
            setNotesModal(!notesModal);
          }}
          rejectDetail={rejectDetail}
        />
      )}

      {/* this drawer is used for the view interview details */}
      {interviewDetailToggle && (
        <InterviewViewDrawer
          toggle={() => setInterviewDetailToggle(!interviewDetailToggle)}
          isOpen={interviewDetailToggle}
          latestInterview={latestInterviewData}
          applicantData={applicantDetail}
          refetch={() => getJobOrderApplicants()}
          setStatusFilter={setStatusFilter}
        />
      )}

      {/* this piece of code is used for sending the candidate calander invite */}
      {calendarInviteModal && (
        <CandidateCalendarInviteModal
          reFetchInterviewEmail={() => {
            getInterviewEmails({
              variables: { interviewId: latestInterviewData?.id },
            });
          }}
          fromInterviewDetailCard={true}
          jobId={applicantDetail?.JobOrder?.id}
          reFetchInterviews={() => getJobOrderApplicants()}
          updateClientCalendarModal={false}
          candidateInterviewEmailData={candidateInterviewEmailData}
          fromEdit={
            getInterviewEmailsData?.getInterviewEmails?.interviewEmails?.find(
              (item: { isToClient: boolean }) => !item?.isToClient
            )
              ? true
              : false
          }
          calendarPrefilledValues={latestInterviewData}
          candidateId={applicantDetail?.candidate?.id}
          name={applicantDetail?.candidate?.fullName}
          type={latestInterviewData?.interviewType}
          interviewId={latestInterviewData?.id}
          calendarInviteModal={calendarInviteModal}
          calendarInviteModalToggle={() =>
            setCalendarInviteModal(!calendarInviteModal)
          }
        />
      )}

      {/* client calander invite drawer serve from here */}
      {clientCalendarInviteModal && (
        <ClientCalendarInviteModal
          reFetchInterviewEmail={() => {
            getInterviewEmails({
              variables: { interviewId: latestInterviewData?.id },
            });
          }}
          skipIt={false}
          from={'interview-card'}
          reFetchInterviews={() => getJobOrderApplicants()}
          jobApplicantId={applicantDetail?.id}
          clientInterviewEmailData={clientInterviewEmailData}
          clientDefaultInviteFromInterview={latestInterviewData}
          fromEdit={
            getInterviewEmailsData?.getInterviewEmails?.interviewEmails?.find(
              (item: { isToClient: boolean }) => item?.isToClient
            )
              ? true
              : false
          }
          jobId={applicantDetail?.JobOrder?.id}
          companyId={applicantDetail?.JobOrder?.company?.id}
          candidateId={applicantDetail?.candidate?.id}
          name={applicantDetail?.candidate?.fullName}
          type={latestInterviewData?.interviewType}
          interviewId={latestInterviewData?.id}
          clientCalendarInviteModal={clientCalendarInviteModal}
          clientCalendarInviteModalToggle={() =>
            setClientCalendarInviteModal(!clientCalendarInviteModal)
          }
        />
      )}

      {/* this is for the create, edit and create counter offer the offer */}
      {offerModal && (
        <CreateEditOffer
          applicantData={applicantDetail}
          setCandidateFilter={setStatusFilter}
          counterOffer={counterOfferToggle}
          fromUpdate={editOfferToggle}
          latestOffer={latestOffer}
          interviewId={latestInterviewData?.id}
          setOfferToggle={() => setOfferModal(!offerModal)}
          offerToggle={offerModal}
          refetch={() => {
            getJobOrderApplicants();
            setPendingInterview(false);
            setPendingSubmission(false);
          }}
          parentToggle={() => { }}
        />
      )}

      {/* this is used for the view offer detail drawer */}
      {/* {offerDrawerToggle && ( */}
      <OfferViewDrawer
        applicantData={applicantDetail}
        toggle={() => setOfferDrawerToggle(!offerDrawerToggle)}
        isOpen={offerDrawerToggle}
        refetch={() => getJobOrderApplicants()}
        setCandidateFilter={setStatusFilter}
      />
      {/* )} */}

      {/* this pop up is used for the replacement confirmation modal*/}
      {replacementModalToggle && (
        <ReplacementModal
          jobApplicants={true}
          isOpen={replacementModalToggle}
          toggle={() => setReplacementModalToggle(!replacementModalToggle)}
          placementId={placementId}
          updateOfferToPlacement={updateOfferToPlacement}
        />
      )}

      {/* this pop up is used for the convert applicant Offer to Placement */}
      {/* <ConfirmPopUp
        confirmText="Are you sure you want to make a placement?"
        isOpen={placementMoveToggle}
        toggle={() => {
          setPlacementMoveToggle(!placementMoveToggle);
        }}
        confirmAction={updateOfferToPlacement}
        modalHeading="Move to Placement"
        btnText="Yes, Move"
        btnColor="primary"
        className="for-modal-styling"
      /> */}

      <ConfirmPopUp
        confirmText="Are you sure you want to change this applicant status?"
        isOpen={confirmBox}
        toggle={setConfirmBox}
        confirmAction={changeApplicantStatus}
        modalHeading="Applicant Status"
        btnText="Confirm"
        btnColor="primary"
      />

      {jobOrder?.id && (
        <BulkAddModal
          isOpen={bulkAddOpen}
          jobOrderId={jobOrder.id}
          onClose={handleBulkAddClose}
        />
      )}
    </div>
  );
};

export default JobApplicantTab;
